import { PlaceRef } from "@/types/entity"
import { CustomMapPlace } from "@/types/inspiration"
import { ItineraryEvent } from "@/types/itinerary"
import {
  CustomPlaceMapMarker,
  ItineraryEventMapMarker,
  MapMarker,
  MapMarkerRef,
  PlaceBasedMapMarker,
  PlaceMapMarker,
  PlacePlaceholderMapMarker,
  WebSearchResultMapMarker,
} from "@/types/map-marker"
import { Place, PlacePlaceholder } from "@/types/place"

import { getPlaceName } from "./gmap-utils"
import { eventLatLong } from "./itinerary-utils"
import { searchResultMapEntityRef } from "./web-search-result-utils"

const MAP_LOCATION_KEY_PRECISION = 4

/** Buckets markers at the same map position (~11m at equator). */
export function formatMapLocationKey(latitude: number, longitude: number) {
  return `${latitude.toFixed(MAP_LOCATION_KEY_PRECISION)},${longitude.toFixed(MAP_LOCATION_KEY_PRECISION)}`
}

export function placeToMapMarker(place: Place): PlaceMapMarker | undefined {
  if (place.latitude == null || place.longitude == null) {
    return undefined
  }

  return {
    markerType: "place" as const,
    title: place.name,
    latitude: place.latitude,
    longitude: place.longitude,
    place,
  }
}

export function placePlaceholderToMapMarker(
  placeholder: PlacePlaceholder
): PlacePlaceholderMapMarker {
  return {
    markerType: "place_placeholder" as const,
    title: placeholder.name,
    latitude: placeholder.latitude,
    longitude: placeholder.longitude,
    placeholder,
  }
}

export function customMapPlaceToMapMarker(
  place: CustomMapPlace
): CustomPlaceMapMarker {
  return {
    markerType: "custom_place" as const,
    title: place.name,
    latitude: place.latitude,
    longitude: place.longitude,
    inspirationItemId: place.inspiration_item_id,
    address: place.address,
  }
}

export function itineraryEventToMapMarker(
  event: ItineraryEvent
): ItineraryEventMapMarker | undefined {
  const latLong = eventLatLong(event)
  if (!latLong) return undefined

  const searchResult = event.properties?.web_search_result

  return {
    markerType: "itinerary_event" as const,
    title: event.title,
    latitude: latLong[0],
    longitude: latLong[1],
    itineraryEventId: event.id,
    itineraryEventType: event.type,
    ...(searchResult
      ? {
          searchResult,
          messageId: event.message_id,
          webSearchResultId: event.web_search_result_id,
        }
      : {}),
  }
}

export function mapMarkerId(marker: MapMarker): MapMarkerRef {
  switch (marker.markerType) {
    case "place":
      return marker.place.entity_ref
    case "place_placeholder":
      return marker.placeholder.entity_ref
    case "itinerary_event":
      return `ie-${marker.itineraryEventId}`
    case "custom_place":
      return `cp-${marker.inspirationItemId}`
    case "web_search_result":
      return searchResultMapEntityRef(marker.messageId, marker.structuredResult)
  }
}

export function isWebSearchResultMarker(
  marker: MapMarker
): marker is WebSearchResultMapMarker {
  return marker.markerType === "web_search_result"
}

export function mapMarkerEntityRef(marker: MapMarker): PlaceRef | undefined {
  if (marker.markerType === "web_search_result") {
    return searchResultMapEntityRef(marker.messageId, marker.structuredResult)
  }
  if (!isPlaceBasedMarker(marker)) return undefined
  return mapMarkerPlace(marker).entity_ref
}

export function mapMarkerPlaceRef(marker: MapMarker): PlaceRef | undefined {
  return mapMarkerEntityRef(marker)
}

export function mapMarkerPlace(
  marker: PlaceBasedMapMarker
): Place | PlacePlaceholder {
  if (marker.markerType === "place") return marker.place
  return marker.placeholder
}

export function mapMarkerDisplayName(marker: MapMarker): string {
  if (marker.markerType === "itinerary_event") {
    return marker.title || "Itinerary event"
  }

  if (marker.markerType === "custom_place") {
    return marker.title
  }

  if (marker.markerType === "web_search_result") {
    return marker.title || marker.searchResult.name
  }

  const place = mapMarkerPlace(marker)
  return getPlaceName(place) || place.name
}

export function isPlaceBasedMarker(
  marker: MapMarker
): marker is PlaceBasedMapMarker {
  return (
    marker.markerType === "place" || marker.markerType === "place_placeholder"
  )
}

export function isItineraryEventMarker(
  marker: MapMarker
): marker is ItineraryEventMapMarker {
  return marker.markerType === "itinerary_event"
}
